import type { Application, ApplicationStatus } from './api';

/**
 * Pure state machine for the Track board's drag-to-move-stage interaction —
 * the applications-board analogue of dragRehome.ts (Refine tab's corpus
 * drag-rehome). Same lifecycle shape, same rationale: kept framework-free
 * (no React) so snapshot / optimistic move / confirm / rollback is
 * unit-testable without mounting a component or a DnD context.
 *
 * Unlike dragRehome (which re-fetches on confirm because a rehome PUT mints
 * a brand-new row id server-side), a stage PATCH returns the SAME
 * application id with just `status` (and possibly appliedAt/closedAt)
 * updated — so confirm here can adopt the server's own returned row directly
 * instead of forcing a full re-fetch. This mirrors PATCH /applications/:id's
 * actual behaviour (applicationTransition.ts's planTransition never mints a
 * new id) rather than copying dragRehome's re-fetch-on-confirm shape where it
 * would be unnecessary.
 *
 * Lifecycle:
 *   idle -> pending (optimistic move applied, snapshot kept)
 *   pending -> idle (confirmed: server's own row adopted, snapshot discarded)
 *   pending -> idle (rolled back: server rejected, snapshot restored)
 * Only one application may be mid-move at a time, matching the UX (you drag
 * one card at once) — the same posture dragRehome.ts documents.
 */

export interface StageMoveState {
  /** The application currently mid-move, or null when idle. */
  pendingId: string | null;
  /** The FULL applications array from just before the optimistic move, so a
   *  failure can restore it byte-for-byte. Null when idle. */
  snapshot: Application[] | null;
}

export const INITIAL_STAGE_MOVE_STATE: StageMoveState = {
  pendingId: null,
  snapshot: null,
};

/** Apply the optimistic move: set `id`'s status to `toStatus` in a NEW array
 *  (never mutate the input), and return both the next list and the state
 *  needed to roll back. A no-op move (already at `toStatus`) is the CALLER's
 *  concern to skip (mirrors useJobsInbox/useCorpusCanvas's pattern of
 *  guarding no-ops before calling into the pure module) — this function
 *  itself always applies the requested move. */
export function applyOptimisticStageMove(
  applications: Application[],
  id: string,
  toStatus: ApplicationStatus,
): { next: Application[]; state: StageMoveState } {
  const next = applications.map((a) => (a.id === id ? { ...a, status: toStatus } : a));
  return {
    next,
    state: { pendingId: id, snapshot: applications },
  };
}

/** The server confirmed — adopt its own returned row (status, plus any
 *  server-stamped appliedAt/closedAt/updatedAt the PATCH set) into the list,
 *  and drop the snapshot. Unlike dragRehome's confirmRehome (which discards
 *  the snapshot and trusts the ALREADY-applied optimistic patch, deferring to
 *  a caller re-fetch for full reconciliation), this adopts the server's row
 *  directly since PATCH /applications/:id returns the complete updated row
 *  in one response — no second round trip needed for the served truth to win. */
export function confirmStageMove(
  applications: Application[],
  serverRow: Application,
): { next: Application[]; state: StageMoveState } {
  const next = applications.map((a) => (a.id === serverRow.id ? serverRow : a));
  return { next, state: INITIAL_STAGE_MOVE_STATE };
}

/** The server rejected (or the request threw) — restore the pre-drag
 *  snapshot. Returns null for `restored` if called with no pending snapshot
 *  (a caller bug, not a user-facing error). */
export function rollbackStageMove(state: StageMoveState): {
  restored: Application[] | null;
  state: StageMoveState;
} {
  return { restored: state.snapshot, state: INITIAL_STAGE_MOVE_STATE };
}

/** Whether an application is currently mid-move (drag/buttons disabled for
 *  others while true, matching the "one card at a time" UX dragRehome.ts
 *  already establishes for the Refine canvas). */
export function isStageMovePending(state: StageMoveState): boolean {
  return state.pendingId !== null;
}
